import { Component, Input, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DatasetService } from 'src/app/services/dataset.service';
import * as XRegExp from 'xregexp';

@Component({
  selector: 'app-create-datasets',
  templateUrl: './create-datasets.component.html',
  styleUrls: ['./create-datasets.component.scss']
})
export class CreateDatasetsComponent implements OnChanges, OnDestroy {
  @Input() accountId = "";
  @Input() fileName = "";
  private unsubscribe$ = new Subject<void>();
  dataSetId: string = "";
  isUpdating: boolean = false;
  dataSetForm!: FormGroup;
  dataSetData: DataSetData["responseData"] = {} as DataSetData["responseData"];
  validDataTypes = ['VARCHAR', 'INT'];

  WORDS_REGX = XRegExp('^[\\p{L}\_\-]*$');


  constructor(
    private dataSetService: DatasetService,
    private router: Router,
    private messageService: MessageService,
    private formBuilder: FormBuilder
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    const fileNameChanged = !!changes['fileName'] && changes['fileName'].currentValue !== changes['fileName'].previousValue;
    if (!this.dataSetForm) {
      this.initializeDataSetForm();
    } else if (fileNameChanged) {
      this.initializeDataSetForm();
    }
    if (fileNameChanged && this.fileName !== "") {
      this.getdataSetByFilename(this.fileName);
    }
  }

  initializeDataSetForm() {
    this.dataSetForm = this.formBuilder.group({
      sourceName: new FormControl('', [Validators.required, Validators.maxLength(200), Validators.pattern(this.WORDS_REGX)]),
      sourceDataSet: this.formBuilder.array([], [this.atLeastOneCheckboxCheckedValidator(), this.allDataTypesSelectedValidator(), this.dataLengthValidator()]),
      sourceindexer: new FormControl(''),
    });
  }

  get sourceDataSetControls() {
    return (this.dataSetForm.get('sourceDataSet') as FormArray).controls;
  }

  getdataSetByFilename(fileName: string) {
    this.dataSetService.getNewDatasetFrom(fileName)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result?.responseData) {
          this.dataSetData = result.responseData;
          this.createDataSetForm(result.responseData);
        }
      });
  }

  createDataSetForm(dataSet: DataSetData["responseData"]) {
    this.dataSetForm.controls['sourceName'].patchValue(dataSet.customerDetails.sourceName);
    this.dataSetForm.controls['sourceindexer'].patchValue(dataSet.sourceIndexer.columns || []);
    const sourceDataSet = dataSet.sourceDataSet;
    const sourceDataSetArray = this.dataSetForm.get('sourceDataSet') as FormArray;
    Object.keys(sourceDataSet).forEach(key => {
      const value = sourceDataSet[key];
      const incomingDataType = value[0];
      const dataTypeValue = this.validDataTypes.includes(incomingDataType) ? incomingDataType : '';
      sourceDataSetArray.push(this.formBuilder.group({
        columnName: new FormControl(key),
        dataType: new FormControl(dataTypeValue, [Validators.required]),
        dataLength: new FormControl(value[1], [Validators.required, Validators.min(1), Validators.max(255), Validators.pattern(/^[0-9]+$/)]),
        isIndexed: new FormControl(false)
      }));
    });
  }

  atLeastOneCheckboxCheckedValidator(): (formArray: AbstractControl) => ValidationErrors | null {
    return (formArray: AbstractControl): ValidationErrors | null => {
      if (!(formArray instanceof FormArray)) {
        return null;
      }
      const isAnyChecked = formArray.controls.some(control => control.get('isIndexed')?.value === true);
      return isAnyChecked ? null : { atLeastOneIndexed: true };
    };
  }

  allDataTypesSelectedValidator(): (formArray: AbstractControl) => ValidationErrors | null {
    return (formArray: AbstractControl): ValidationErrors | null => {
      if (!(formArray instanceof FormArray)) {
        return null;
      }
      const isAnyEmpty = formArray.controls.some(control => {
        const dataType = control.get('dataType')?.value;
        return dataType === null || dataType === '';
      });
      return isAnyEmpty ? { dataTypeRequired: true } : null;
    };
  }

  dataLengthValidator(): (formArray: AbstractControl) => ValidationErrors | null {
    return (formArray: AbstractControl): ValidationErrors | null => {
      if (!(formArray instanceof FormArray)) {
        return null;
      }
      const isAnyInvalid = formArray.controls.some(control => {
        const dataLengthControl = control.get('dataLength');
        // Check if the control itself has any errors (like required, min, max, pattern)
        return dataLengthControl?.invalid;
      });
      return isAnyInvalid ? { invalidDataLength: true } : null;
    };
  }

  submitForm() {
    if (this.dataSetForm.invalid) {
      this.dataSetForm.markAllAsTouched();
      return;
    }
    let formData = this.dataSetForm.value;
    const sourceDataSet = formData.sourceDataSet.reduce((acc: any, item: any) => {
      acc[item.columnName] = [item.dataType, item.dataLength];
      return acc;
    }, {});
    let request = {
      customerDetails: {
        enrollmentId: this.dataSetData.customerDetails.enrollmentId,
        accountId: this.dataSetData.customerDetails.accountId,
        sourceName: formData.sourceName,
        payloadLocation: this.dataSetData.customerDetails.payloadLocation,
        updateFrequency: this.dataSetData.customerDetails.updateFrequency
      },
      sourceDataSet: sourceDataSet,
      sourceIndexer: {
        columns: formData.sourceindexer
      }
    };
    this.sendDatasetPayload(request);
  }

  sendDatasetPayload(body: DatasetFromRequest) {
    if (!this.isUpdating) {
      this.dataSetService.createDataset(body)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((result) => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: result?.responseData?.message });
        });
    } else {
      this.dataSetService.updateDataset(body, this.dataSetId)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((result) => {
          //  this.messageService.add({ severity: 'success', summary: 'Success', detail: result?.responseData?.status });
          //  this.router.navigate(['/config/account', this.accountId, 'dataset', result?.responseData?.configurationId, 'view']);
        });
    }
  }

  onIndexerChange(event: Event, columnName: any) {
    if (!columnName) return;

    const checked = (event.target as HTMLInputElement).checked;
    const sourceIndexerControl = this.dataSetForm.get('sourceindexer') as FormControl;
    let indexerColumns: string[] = sourceIndexerControl.value || [];

    if (checked) {
      indexerColumns.push(columnName.value);
    } else {
      const index = indexerColumns.indexOf(columnName.value);
      if (index > -1) {
        indexerColumns.splice(index, 1);
      }
    }
    sourceIndexerControl.setValue(indexerColumns);
  }

  getUpdatedDataSetByFileName(fileName: string) {
    console.log('to be done');
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
